export type AuthErrorCode =
  | 'cancelled'
  | 'invalid-credentials'
  | 'user-disabled'
  | 'too-many-requests'
  | 'network'
  | 'not-supported'
  | 'config'
  | 'no-access'
  | 'tenant-suspended'
  | 'email-not-verified'
  | 'google-not-configured'
  | 'google-auth-failed'
  | 'google-grant-revoked'
  | 'google-unavailable'
  | 'invalid-state'
  | 'session-revoked'
  | 'unknown';

/** Normalized error thrown by every adapter so the UI never deals with SDK-specific codes. */
export class AuthError extends Error {
  readonly code: AuthErrorCode;
  readonly cause?: unknown;

  constructor(code: AuthErrorCode, message?: string, cause?: unknown) {
    super(message ?? code);
    this.name = 'AuthError';
    this.code = code;
    this.cause = cause;
  }
}

/** User-facing messages (pt-BR). Translate here to localize the whole auth flow. */
const MESSAGES: Record<AuthErrorCode, string> = {
  cancelled: 'Login cancelado.',
  'invalid-credentials': 'Não foi possível confirmar o seu login. Tente de novo.',
  'user-disabled': 'Esta conta foi desativada.',
  'too-many-requests': 'Muitas tentativas. Aguarde um pouco e tente novamente.',
  network: 'Sem conexão com o servidor. Verifique sua internet.',
  'not-supported': 'Este método de login não está disponível.',
  config: 'O login não está configurado corretamente.',
  'no-access':
    'Sua conta ainda não tem acesso a este espaço. Peça um convite ao responsável e entre com o mesmo e-mail.',
  'tenant-suspended': 'Este espaço está temporariamente indisponível.',
  'email-not-verified': 'Seu e-mail do Google ainda não foi verificado.',
  'google-not-configured': 'O login com Google ainda não foi configurado neste servidor.',
  'google-auth-failed': 'O Google não confirmou o login. Tente de novo.',
  'google-grant-revoked': 'A autorização do Google expirou. Tente de novo.',
  'google-unavailable': 'O Google não respondeu. Tente de novo em instantes.',
  'invalid-state': 'O link de login expirou. Comece de novo.',
  'session-revoked': 'Sua sessão foi encerrada. Entre novamente.',
  unknown: 'Algo deu errado. Tente novamente.',
};

export const KNOWN_AUTH_CODES = new Set<string>(Object.keys(MESSAGES));

export function toAuthError(error: unknown): AuthError {
  if (error instanceof AuthError) return error;
  if (error instanceof TypeError && /fetch|network/i.test(error.message)) {
    return new AuthError('network', error.message, error);
  }
  return new AuthError('unknown', error instanceof Error ? error.message : String(error), error);
}

/** Message for an error object or for an error code (e.g. from `?error=` after a redirect). */
export function getAuthErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return KNOWN_AUTH_CODES.has(error) ? MESSAGES[error as AuthErrorCode] : MESSAGES.unknown;
  }
  return MESSAGES[toAuthError(error).code];
}
